import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "../../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../components/ui/command";
import {
  CAMPAIGN_LOGIN_STATUS,
  getAuthorizedCampaigns,
  loginToCampaign,
} from "../../../lib/telephonyCampaignLoginApi";
import { campaignLoginFailureMessage } from "../campaignLoginFailureCopy";
import {
  campaignCountLabel,
  filterCampaigns,
  sortCampaigns,
} from "../campaignSelection";

const LOAD_STATE = Object.freeze({
  LOADING: "loading",
  LOADED: "loaded",
  NOT_PROVISIONED: "not-provisioned",
  UNAVAILABLE: "unavailable",
  ERROR: "error",
});

const LOGIN_STATE = Object.freeze({
  IDLE: "idle",
  PENDING: "pending",
  FAILED: "failed",
});

function formatAvailableLeads(availableLeads) {
  if (availableLeads === null || availableLeads === undefined) return "—";
  return availableLeads.toLocaleString();
}

/**
 * Pre-session Workspace state (BB-6-login-work-order Stage-6
 * completion). Real, authorized campaign data via
 * lib/telephonyCampaignLoginApi.js -- no fixture campaign, no local-
 * only preview behavior. Compact/searchable, scales to ~40 campaigns
 * without a giant-card layout.
 *
 * Never shows an optimistic Logged In-Paused transition: `onLogin` is
 * only called after the backend's own POST /login response reports
 * `state === "logged_in_paused"` -- the backend's own SESSION_LOGIN
 * executor (PR #307) has already completed its fresh, observation-
 * gated confirmation by that point.
 */
export default function WorkspacePreviewGate({ onLogin }) {
  const [loadState, setLoadState] = useState(LOAD_STATE.LOADING);
  const [campaigns, setCampaigns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [loginState, setLoginState] = useState(LOGIN_STATE.IDLE);
  const [loginFailureMessage, setLoginFailureMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const result = await getAuthorizedCampaigns();
        if (cancelled) return;
        if (result.status === CAMPAIGN_LOGIN_STATUS.OK) {
          setCampaigns(sortCampaigns(result.campaigns));
          setLoadState(LOAD_STATE.LOADED);
        } else if (result.status === CAMPAIGN_LOGIN_STATUS.NOT_PROVISIONED) {
          setLoadState(LOAD_STATE.NOT_PROVISIONED);
        } else {
          setLoadState(LOAD_STATE.UNAVAILABLE);
        }
      } catch (error) {
        if (!cancelled) setLoadState(LOAD_STATE.ERROR);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredCampaigns = useMemo(
    () => filterCampaigns(campaigns, searchTerm),
    [campaigns, searchTerm],
  );

  const selectedCampaign = campaigns.find((campaign) => campaign.campaign_id === selectedCampaignId) || null;

  async function handleLogin() {
    if (!selectedCampaign || loginState === LOGIN_STATE.PENDING) return;
    setLoginState(LOGIN_STATE.PENDING);
    setLoginFailureMessage(null);
    try {
      const result = await loginToCampaign(selectedCampaign.campaign_id);
      if (result.state === "logged_in_paused") {
        onLogin(selectedCampaign);
        return;
      }
      setLoginState(LOGIN_STATE.FAILED);
      setLoginFailureMessage(campaignLoginFailureMessage(result.failure_reason));
    } catch (error) {
      setLoginState(LOGIN_STATE.FAILED);
      setLoginFailureMessage(campaignLoginFailureMessage(null));
    }
  }

  return (
    <section
      aria-label="Campaign entry"
      className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-16"
      data-testid="workspace-preview-gate"
    >
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-[16px] border border-white/10 bg-white/[0.02] p-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-slate-100">Campaign login</h2>
          <p className="text-sm text-slate-500">Select a campaign, then choose Login.</p>
        </div>

        {loadState === LOAD_STATE.LOADING ? (
          <div
            className="flex items-center justify-center gap-2 rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-6 text-sm text-slate-400"
            data-testid="workspace-campaigns-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading campaigns…
          </div>
        ) : null}

        {loadState === LOAD_STATE.NOT_PROVISIONED ? (
          <p
            className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-3 text-sm text-slate-400"
            data-testid="workspace-campaigns-not-provisioned"
          >
            Your dialer access isn't set up yet. Contact your administrator.
          </p>
        ) : null}

        {loadState === LOAD_STATE.UNAVAILABLE ? (
          <p
            className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-3 text-sm text-slate-400"
            data-testid="workspace-campaigns-unavailable"
          >
            Campaign login is unavailable right now.
          </p>
        ) : null}

        {loadState === LOAD_STATE.ERROR ? (
          <p
            className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-3 text-sm text-slate-400"
            data-testid="workspace-campaigns-error"
          >
            The dialer is temporarily unavailable. Try again in a moment.
          </p>
        ) : null}

        {loadState === LOAD_STATE.LOADED && campaigns.length === 0 ? (
          <p
            className="rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-3 text-sm text-slate-400"
            data-testid="workspace-campaigns-empty"
          >
            No campaigns are available for login right now.
          </p>
        ) : null}

        {loadState === LOAD_STATE.LOADED && campaigns.length > 0 ? (
          <Command
            className="rounded-[10px] border border-white/10 bg-white/[0.03]"
            data-testid="workspace-campaign-selector"
          >
            <CommandInput
              placeholder="Search campaigns…"
              value={searchTerm}
              onValueChange={setSearchTerm}
              data-testid="workspace-campaign-search"
            />
            <CommandList className="max-h-56" data-testid="workspace-campaign-list">
              <CommandEmpty>No campaigns match your search.</CommandEmpty>
              {filteredCampaigns.map((campaign) => (
                <CommandItem
                  key={campaign.campaign_id}
                  value={campaign.campaign_name}
                  onSelect={() => setSelectedCampaignId(campaign.campaign_id)}
                  data-testid={`workspace-campaign-option-${campaign.campaign_id}`}
                  data-selected={campaign.campaign_id === selectedCampaignId}
                  className="flex items-center justify-between"
                >
                  <span className="truncate text-slate-100">{campaign.campaign_name}</span>
                  <span
                    className="ml-3 shrink-0 text-xs text-slate-500"
                    data-testid={`workspace-campaign-dialable-${campaign.campaign_id}`}
                  >
                    {formatAvailableLeads(campaign.available_leads)} available
                  </span>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        ) : null}

        {loadState === LOAD_STATE.LOADED && campaigns.length > 0 ? (
          <p
            className="text-xs text-slate-500"
            data-testid="workspace-campaign-count"
          >
            {campaignCountLabel({
              filteredCount: filteredCampaigns.length,
              totalCount: campaigns.length,
            })}
          </p>
        ) : null}

        {selectedCampaign ? (
          <div
            className="flex items-center justify-between rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-2"
            data-testid="workspace-campaign-identity"
          >
            <span className="text-xs uppercase tracking-wide text-slate-500">Campaign</span>
            <span className="truncate text-sm font-medium text-slate-200">{selectedCampaign.campaign_name}</span>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            onClick={handleLogin}
            disabled={!selectedCampaign || loginState === LOGIN_STATE.PENDING}
            data-testid="workspace-login-button"
          >
            {loginState === LOGIN_STATE.PENDING ? (
              <span className="flex items-center gap-2" data-testid="workspace-login-pending">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Logging in…
              </span>
            ) : (
              "Login"
            )}
          </Button>

          {loginState === LOGIN_STATE.FAILED ? (
            <p
              className="text-xs leading-snug text-red-300"
              role="alert"
              data-testid="workspace-login-failure"
            >
              {loginFailureMessage}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
